<template>
  <section>
    <errors-list :errorMessage="errorMessage" />
    <v-data-table
      :headers="translatedHeaders"
      :items="items"
      :loading="loading"
      :rows-per-page-items="rowsPerPageItems"
      :total-items="pagination.totalItems"
      :pagination.sync="pagination"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr v-bind:class="{ 'red lighten-4': props.item.hasErrors }">
          <grid-cell
            :header="header"
            :row="props.item"
            v-for="(header, index) in translatedHeaders"
            :key="index"
            @action="onAction"
          ></grid-cell>
        </tr>
      </template>
    </v-data-table>
  </section>
</template>

<script>
import GridCell from '../../components/GridCell';
import { serializeQuery } from '../../utils';
import { getErrorMessage } from '../../errorHandlers';
import ErrorsList from '../../components/ErrorsList';
import { defaultDateTimeFormatter } from '../../utils';
import { ContractStatusEnum } from '../../constants';

export const contractStatusFieldMap = {
  created: '@insurance/createdStatusLabel',
  offerAccepted: '@insurance/offerAcceptedStatusLabel',
  toBeTerminated: '@insurance/toBeTerminatedStatusLabel',
  terminated: '@insurance/terminatedStatusLabel',
  terminating: '@insurance/terminatingStatusLabel',
};

export const defaultHeaders = [
  { text: '@insurancesList/contractIdColumnHeader', sortable: true, value: 'internalId' },
  { text: '@insurancesList/productNameColumnHeader', sortable: true, value: 'product.name' },
  { text: '@insurancesList/monthlyCostColumnHeader', sortable: true, value: 'cost' },
  { text: '@insurancesList/AgentNameColumnHeader', sortable: true, value: 'agent.fullName' },
  {
    text: '@insurancesList/updatedAtColumnHeader',
    value: 'updated',
    sortable: true,
    format: defaultDateTimeFormatter,
  },
];

export const sortFieldMap = {
  'contactPerson.ssn': 'customer.socialSecurityNumber',
  'contactPerson.fullName': 'customer.firstName',
  internalId: 'insurance.internalId',
  'product.name': 'product.name',
  cost: 'insurance.periodCost',
  'agent.fullName': 'user.firstName',
  updated: 'insurance.updated',
  status: 'insurance.status',
};

const eventToStatusMap = {
  InsuranceCreated: ['created'],
  InsuranceAccepted: ['offerAccepted', 'created'],
  InsuranceTerminated: ['terminated', 'created', 'offerAccepted'],
};

export default {
  components: { GridCell, ErrorsList },
  props: {
    headers: {
      type: Array,
      default: () => [...defaultHeaders],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 15,
        sortBy: 'updated',
        totalItems: 0,
      },
      rowsPerPageItems: [10, 15, 50, 100],
      items: [],
      loading: false,
      errorMessage: '',
      lastQueryString: '',
    };
  },

  watch: {
    pagination() {
      this.fetchData();
    },

    filters() {
      this.fetchData();
    },
  },

  computed: {
    translatedHeaders() {
      return Array.isArray(this.headers)
        ? this.headers.map((h) => ({
            ...h,
            text: this.$t(h.text),
          }))
        : [];
    },
    query() {
      const offset = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const limit = this.pagination.rowsPerPage;
      const query = { ...this.filters, offset, limit };

      delete query.invoiceLetterStatus;
      delete query.invoiceType;
      delete query.invoiceStatus;

      if (query.status === 'unpaidPaperInvoice') {
        query.status = 'offerAccepted';
        query.invoiceLetterStatus = 'sent';
        query.invoiceType = 'paper';
        query.invoiceStatus = 'pending';
      }

      if (query.status && !Object.keys(ContractStatusEnum).includes(query.status)) {
        delete query.status;
      }

      if (this.pagination.sortBy) {
        query.sortBy = sortFieldMap[this.pagination.sortBy];
        query.sortOrder = this.pagination.descending ? 'DESC' : 'ASC';
        return query;
      } else {
        // eslint-disable-next-line no-unused-vars
        const { sortBy, sortOrder, ...rest } = query;
        return rest;
      }
    },
  },

  methods: {
    onAction({ action, row }) {
      if (!action) {
        this.$emit('selected', row);
      }
    },

    async fetchData() {
      const qs = serializeQuery(this.query);
      if (this.lastQueryString === qs || this.loading) {
        return;
      }

      try {
        this.loading = true;
        this.$emit('beforeRequest', this.query);
        const {
          data: { data, meta },
        } = await this.$http.get(`/api/insurances?${qs}`);
        this.items = data;

        this.pagination.totalItems = meta.total;
        this.lastQueryString = qs;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },
  },

  sockets: {
    insuranceSubscribe: function(payload) {
      const affectedStatuses = payload && eventToStatusMap[payload.type];

      // Refresh list if ws event affects scope of the displayed data
      if (
        affectedStatuses &&
        (affectedStatuses.includes(this.query.status) || !this.query.status)
      ) {
        this.lastQueryString = '';
        this.fetchData();
      }
    },
  },
};
</script>
